import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Video,
  Building
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const InterviewsSection = ({ candidateId }) => {
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);

  useEffect(() => {
    loadInterviews();
  }, [candidateId]);

  const loadInterviews = async () => {
    try {
      setIsLoading(true);
      // Simuler des données d'entretiens pour l'instant
      // TODO: Remplacer par l'API réelle
      const mockInterviews = [
        {
          id: 1,
          date: "2024-01-15",
          time: "14:00",
          type: "Présentiel",
          location: "Bureau Paris",
          interviewer: "Marie Dupont",
          interviewerPhone: "01 23 45 67 89",
          interviewerEmail: "marie.dupont@entreprise.com",
          status: "Planifié",
          notes: "Premier entretien technique",
          client: "Entreprise ABC"
        },
        {
          id: 2,
          date: "2024-01-10",
          time: "10:30",
          type: "Visioconférence",
          location: "Teams",
          interviewer: "Jean Martin",
          interviewerPhone: "01 98 76 54 32",
          interviewerEmail: "jean.martin@client.com",
          status: "Terminé",
          notes: "Entretien RH très positif",
          client: "Client XYZ"
        }
      ];
      
      setTimeout(() => {
        setInterviews(mockInterviews);
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Erreur lors du chargement des entretiens:", error);
      toast.error("Erreur lors du chargement des entretiens");
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Planifié":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Terminé":
        return "bg-green-100 text-green-800 border-green-200";
      case "Annulé":
        return "bg-red-100 text-red-800 border-red-200";
      case "Reporté":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Planifié":
        return <Clock className="w-4 h-4" />;
      case "Terminé":
        return <CheckCircle className="w-4 h-4" />;
      case "Annulé":
        return <XCircle className="w-4 h-4" />;
      case "Reporté":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Entretiens</h2>
          <p className="text-gray-600 mt-1">
            Gérez les entretiens programmés pour ce candidat
          </p>
        </div>
        <motion.button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-4 h-4" />
          <span>Nouvel entretien</span>
        </motion.button>
      </div>

      {/* Liste des entretiens */}
      {interviews.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun entretien programmé
          </h3>
          <p className="text-gray-600 mb-4">
            Commencez par planifier un entretien pour ce candidat
          </p>
          <motion.button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Planifier un entretien
          </motion.button>
        </motion.div>
      ) : (
        <div className="grid gap-6">
          {interviews.map((interview, index) => (
            <motion.div
              key={interview.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {interview.type === "Visioconférence" ? (
                      <Video className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Building className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Entretien - {interview.client}
                    </h3>
                    <p className="text-gray-600">{interview.type}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(interview.status)}`}>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(interview.status)}
                      <span>{interview.status}</span>
                    </div>
                  </span>
                  <div className="flex space-x-1">
                    <motion.button
                      onClick={() => setEditingInterview(interview)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Edit className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      onClick={() => {
                        if (window.confirm("Êtes-vous sûr de vouloir supprimer cet entretien ?")) {
                          setInterviews(interviews.filter(i => i.id !== interview.id));
                          toast.success("Entretien supprimé");
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center space-x-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(interview.date)}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{interview.time}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{interview.location}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center space-x-2 text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{interview.interviewer}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{interview.interviewerPhone}</span>
                </div>
              </div>

              {interview.notes && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>Notes :</strong> {interview.notes}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* TODO: Ajouter les modals pour créer/éditer les entretiens */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nouvel entretien</h3>
            <p className="text-gray-600 mb-4">
              Fonctionnalité en cours de développement...
            </p>
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewsSection;
